import { PageEntityType } from "../../services/ChatService";

/*
 * The AI chat's link vocabulary: entity detail pages (ENTITY_ROUTES) and static pages with no id
 * segment (STATIC_LINK_TARGETS). renderMarkdown and ChatPanel derive the deep-link whitelist from
 * the linkTarget rows and the static pages, while pageContext matches the full entity row set to
 * tell the assistant which entity page the admin is viewing.
 *
 * Rows without linkTarget are context-only and must never become linkable.
 *
 * The linkTarget rows and PAGE_ENTITY_TYPES are checked against admin-mcp's committed snapshot by
 * __tests__/entityRoutes.contract.test.ts — see "Entity link contract" in
 * rest-services/admin-mcp/CLAUDE.md. Still kept in sync by hand: STATIC_LINK_TARGETS with
 * admin-mcp's BackOfficePage, the system prompt link instructions (config/ChatSystemPrompt), the
 * page-form snapshot and field-update validation (PageFormFormatter, FormTools — the PageForms /
 * FormUpdate records in ChatController, typed on this side in services/ChatService.ts), and the
 * standalone POC page (static/chat.html), which copies both lists.
 */

interface EntityRoute {
  /** react-router v5 pattern for a detail page showing this entity. */
  path: string;
  entityType: PageEntityType;
  /** The canonical detail page the assistant is allowed to deep-link to (one per entity). */
  linkTarget?: boolean;
}

export const ENTITY_ROUTES: EntityRoute[] = [
  { path: "/user/edit/:id", entityType: "user" },
  { path: "/user/cashbacks/:id", entityType: "user" },
  { path: "/user/next/cashback/:id", entityType: "user" },
  { path: "/user/:id", entityType: "user", linkTarget: true },
  { path: "/account/edit/:id", entityType: "account" },
  { path: "/account/:id", entityType: "account", linkTarget: true },
  { path: "/transaction/:id", entityType: "transaction", linkTarget: true },
  { path: "/payment/:id", entityType: "payment", linkTarget: true },
  { path: "/admin/admin-user/:id", entityType: "admin-user", linkTarget: true },
  { path: "/bonus/view/:id", entityType: "user-bonus", linkTarget: true },
  { path: "/bonus/config/:id", entityType: "bonus-config", linkTarget: true },
  { path: "/casino-game/:id", entityType: "casino-game", linkTarget: true },
];

export const ENTITY_ID_PATTERN = /^[A-Za-z0-9_.-]+$/;

const LINK_PREFIXES = ENTITY_ROUTES.filter((route) => route.linkTarget).map((route) => route.path.replace(/\/:id$/, ""));

/**
 * True only for "{linkable detail page}/{single id segment}" URLs, so the model (or an
 * injected tool result) can never produce an arbitrary href.
 */
export function isInternalEntityLink(url: string): boolean {
  const prefix = LINK_PREFIXES.find((candidate) => url.startsWith(candidate + "/"));
  return prefix !== undefined && ENTITY_ID_PATTERN.test(url.slice(prefix.length + 1));
}

/** Non-entity pages the assistant may link to or open; matched exactly, with no id segment. */
export const STATIC_LINK_TARGETS: readonly string[] = ["/admin/profile"];

/** The one check both link rendering and assistant-requested navigation go through. */
export function isInternalLink(url: string): boolean {
  return isInternalEntityLink(url) || STATIC_LINK_TARGETS.includes(url);
}
